import * as filepath from "./filepath";

import { FsDir, kInvalidEntry } from "./fsentry";
import { inflect, len, throwIf } from "./util";

import { FS } from "./fs";
import type { HttpProgress } from "./http";
import type { LocalConfig } from "./fs";
import type { Writable } from "svelte/store";

// Note: can't type dh as FileSystemDirectoryHandle
// because lib.dom.ts in vscode doesn't have
// entries() value
async function dirHandleReadDir(dir: FsDir, dh: any): Promise<void> {
  for await (const entry of dh.values()) {
    let isFile = entry.kind === "file";
    let isFolder = entry.kind === "directory";
    throwIf(!isFolder && !isFile, "must be file or folder");

    if (isFile) {
      const f: File = await entry.getFile();
      dir.addFile(f.name, f.size, f.lastModified);
    } else {
      dir.addDir(entry.name);
    }
  }
}

export class FileSystemLocal extends FS {
  constructor(localConfig: LocalConfig) {
    super(localConfig);
  }

  get localConfig(): LocalConfig {
    return this.config as LocalConfig;
  }

  get dirHandle() {
    return this.localConfig.dirHandle;
  }

  async readDir(
    dirPath: string,
    force: boolean,
    progress: Writable<any>
  ): Promise<FsDir> {
    throwIf(dirPath == "", `empty dirPath`);
    return this.loadDir(dirPath, force, progress, async () => {
      let dh = await this.directoryHandle(dirPath);
      let dir = new FsDir(dirPath, dh);
      await dirHandleReadDir(dir, dh);
      return dir;
    });
  }

  private async directoryHandle(dirPath: string): Promise<FileSystemDirectoryHandle> {
    if (dirPath == "/") {
      return this.dirHandle;
    }
    let dirNames = [];
    let currDir = dirPath;
    let dh: FileSystemDirectoryHandle | null = null;
    while (!dh) {
      let parts = filepath.split(currDir);
      dirNames.push(parts.name);
      let dirParent = parts.dir;
      if (dirParent == "/") {
        dh = this.dirHandle;
      } else {
        let de = this.dirs.get(dirParent);
        if (de && de.handle) {
          dh = de.handle;
        } else {
          currDir = dirParent;
        }
      }
    }
    let n = len(dirNames);
    for (let i = n - 1; i >= 0; i--) {
      let name = dirNames[i];
      dh = await dh.getDirectoryHandle(name);
      currDir = filepath.join(currDir, name);
      if (!dh) {
        throw new Error(`no sub-directory '${name}' in directory '${currDir}`);
      }
    }
    return dh;
  }

  async deleteDir(dirPath: string) {
    throwIf(dirPath == "/", "can't delete '/' directory");

    let parts = filepath.split(dirPath);
    let de = this.dirs.get(parts.dir);
    if (!de) {
      throw new Error(`didn't find parent directory '${parts.dir}' for dir '${dirPath}'`);
    }

    let fileName = parts.name;
    await de.handle!.removeEntry(fileName, { recursive: true });
    this.removeCachedDirRecur(dirPath);
    de.removeChildByName(fileName);
  }

  async deleteFile(path: string) {
    console.log(`FileSystemLocal.deleteFile: ${path}`);
    if (isDirectory(path)) {
      path = stiripSlashEnd(path);
      if (this.dirs.has(path)) {
        return await this.deleteDir(path);
      }
      throw new Error(`didn't find FSEntry for ${path}`);
    }

    let df = this.findDirAndFileFSEntry(path);
    if (!df || df.entry === kInvalidEntry) {
      throw new Error(`'${path}' not found`);
    }
    let name = df.dir.entryName(df.entry);
    // TODO: need to remove deleted directories from df.
    if (df.dir.handle) {
      await df.dir.handle.removeEntry(name);
    }
  }

  async deleteFiles(paths: string[], progress: Writable<any>) {
    const n = len(paths);
    const fileStr = inflect("file", n);
    progress.set(`deleting ${n} ${fileStr}`);
    for (let path of paths) {
      progress.set(`deleting file ${path}`);
      await this.deleteFile(path);
    }
  }

  async createParentDir(dirPath: string): Promise<FsDir | undefined> {
    console.log(`FSLocal.createParentDir: path='${dirPath}'`);
    if (dirPath == "/" || dirPath == "") {
      return undefined;
    }
    let df = filepath.split(dirPath);
    let parentDirPath = df.dir;
    let dirParent = this.dirs.get(parentDirPath);
    if (!dirParent) {
      dirParent = await this.createParentDir(parentDirPath);
      if (!dirParent) {
        return undefined;
      }
    }
    let dh = dirParent.handle;
    if (!dh) {
      throw new Error(`no directory handle for ${parentDirPath}`);
    }
    let newDH = await dh.getDirectoryHandle(df.name, { create: true });

    // add a directory stub to the parent listing
    dirParent.addDir(df.name);

    // needs an entry with full dir
    let de = new FsDir(dirPath, newDH);
    this.cacheDir(de);
    return de;
  }

  async createDirectory(dirPath: string) {
    let de = await this.createParentDir(dirPath);
    if (!de) {
      throw new Error(`couldn't create directory ${dirPath}`);
    }
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress) {
    console.log(`local.uploadFile path: '${path}'`);
    let df = filepath.split(path);
    let d = this.dirs.get(df.dir);
    if (!d) {
      d = await this.createParentDir(df.dir);
      if (!d) {
        return;
      }
    }
    let fileName = df.name;
    let newHandle = await d.handle!.getFileHandle(fileName, {
      create: true,
    });
    await writeFile(newHandle, file);

    let lastMod = 0; // TODO: use new Date().getTime() ?
    d.addFile(fileName, file.size, lastMod);
  }

  async getPublicURL(path: string): Promise<string> {
    console.log(`FSLocal.getPublicURL: path=${path}`);

    let file = await this.downloadFile(path);
    let uri = URL.createObjectURL(file);
    return uri;
  }

  async downloadFile(path: string, httpProgress?: HttpProgress): Promise<Blob> {
    // TODO: should work even if we don't have info cached
    // maybe call readDir(dir), which takes care of that
    let dn = filepath.split(path);
    let de = this.dirs.get(dn.dir);
    if (!de) {
      throw new Error(`no directory ${dn.dir} for file ${path}`);
    }
    let fh = await de.handle!.getFileHandle(dn.name);
    if (!fh) {
      throw new Error(`getFileHandle(${dn.name}) failed`);
    }
    let file = await fh.getFile();
    if (!file) {
      throw new Error(`getFile() failed`);
    }
    console.log("file:", file);
    return file;
  }

  /*
    // Get references to a file and a directory.
  const [file] = await showOpenFilePicker();
  const directory = await showDirectoryPicker();
  
  // Rename the file.
  await file.rename('new_name');
  // Move the file to a new directory.
  await file.move(directory);
  // Move the file to a new directory and rename it.
  await file.move(directory, 'newer_name');
  */

  /*
  rename2(path, newPath, onDone) {
    // TODO: implement rename by uploadFile() / deleteOne
    throwIf(true, "rename not supported ");
    console.log(`FSLocal.rename: path=${path}, newPath=${newPath}`);
    throwIf(path == newPath, "path must be != newPath");
    let dn = filepath.split(path);
    let dnDest = filepath.split(newPath);
    throwIf(dn.dir != dnDest.dir, "must be in same directory");
    let d = this.dirs.get(dn.dir);
    if (!d) {
      onDone(new Error(`no directory ${dn.dir} found`));
      return;
    }

    const ren = async () => {
      try {
        console.log(`renaming ${dn.name} to ${dnDest.name}`);
        let fh = await d.dirHandle.getFileHandle(dn.name);
        await fh.rename(dnDest.name);
        onDone(null);
      } catch (e) {
        onDone(e);
      }
    };

    ren();
  }
*/

  async rename(path: string, newPath: string) {
    console.log(`FSLocal.rename: path=${path}, newPath=${newPath}`);
    throwIf(path == newPath, "path must be != newPath");
    let dn = filepath.split(path);
    let dnDest = filepath.split(newPath);
    throwIf(dn.dir != dnDest.dir, "must be in same directory");
    let d = this.dirs.get(dn.dir);
    if (!d) {
      throw new Error(`no directory ${dn.dir} found`);
    }
    console.log(`renaming ${dn.name} to ${dnDest.name}`);
    let dh = d.handle;
    if (!dh) {
      throw new Error(`no directory handle for ${dn.dir}`);
    }
    let fhIn = await dh.getFileHandle(dn.name);
    let fhOut = await dh.getFileHandle(dnDest.name, { create: true });
    // @ts-ignore
    let writable = await fhOut.createWritable();
    let file = await fhIn.getFile();
    await writable.write(file);
    await writable.close();
    await dh.removeEntry(dn.name);
  }
}

async function writeFile(fileHandle: any, contents: Blob) {
  // Create a FileSystemWritableFileStream to write to.
  const writable = await fileHandle.createWritable();
  // Write the contents of the file to the stream.
  await writable.write(contents);
  // Close the file and write the contents to disk.
  await writable.close();
}

export function makeFileSystemLocal(localConfig: LocalConfig): FileSystemLocal {
  return new FileSystemLocal(localConfig);
}

function isDirectory(path: string): boolean {
  return path.endsWith("/");
}

function stiripSlashEnd(path: string): string {
  let n = len(path);
  return path.substring(0, n - 1);
}
